import React from "react";
import { TicketCard } from "./TicketCard";
import { motion, AnimatePresence } from "motion/react";
import { useDrop } from "react-dnd";
import { TicketInterface } from "../../../../models/TicketModule/Ticket";
import { StatusInterface } from "../../../../models/TicketModule/Status";
import { lightenColor } from "./KanbanColumn";
import { useLanguageStore } from "../../../../store/useLanguageStore";

interface WeekViewProps {
  tickets: TicketInterface[];
  statuses: StatusInterface[];
  currentWeekStart: Date;
  onTicketMove: (ticketId: string, newStatusId: string) => void;
  darkMode: boolean;
  rangeStart?: Date;
  rangeEnd?: Date;
  useCustomRange?: boolean;
}

function StatusColumn({
  status,
  tickets,
  onTicketMove,
  index,
}: {
  status: StatusInterface;
  tickets: TicketInterface[];
  onTicketMove: (ticketId: string, newStatusId: string) => void;
  index: number;
}) {
  const { language } = useLanguageStore();

  const color = status.badgeColor || "#F3F4F6";

  const [{ isOver }, drop] = useDrop(() => ({
    accept: "TICKET",
    drop: (item: { id: string }) => {
      onTicketMove(item.id, status.id || "");
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  }));

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.08 }}
      className="flex-1 min-w-[320px] max-w-[380px] bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
    >
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="bg-white px-4 py-3 flex items-center justify-between border-b border-gray-200">
          <div className="flex items-center gap-2.5">
            <div className={`w-2 h-2 rounded-full`} style={{ backgroundColor: color }}></div>
            <div
              className={`text-[11px] px-2 py-0.5 rounded-md font-medium uppercase tracking-wide`}
              style={{ color: color, backgroundColor: lightenColor(color, 0.85) }}
            >
              {status.title}
            </div>
            <div className="bg-gray-100 text-gray-500 text-[11px] px-2 py-0.5 rounded-md min-w-[24px] text-center font-medium">
              {tickets.length}
            </div>
          </div>
        </div>

        {/* Cards Area */}
        <div
          ref={drop}
          className={`flex-1 p-3 space-y-2.5 transition-all duration-300 ease-in-out relative overflow-y-visible 
          }`}
          style={{
            backgroundColor: isOver ? lightenColor(color, 0.85) : "transparent",
            minHeight: 300,
          }}
        >
          {isOver && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className={`absolute inset-3 rounded-lg border-2 border-dashed  bg-white/50 backdrop-blur-sm pointer-events-none z-10`}
              transition={{ duration: 0.2 }}
              style={{ borderColor: lightenColor(color, 0.5) }}
            >
              <div className="absolute inset-0 flex items-center justify-center">
                <div className={`text-sm font-medium bg-white px-4 py-2 rounded-lg shadow-sm`} style={{ color: color }}>
                  {language["DROP_HERE"]}
                </div>
              </div>
            </motion.div>
          )}

          <AnimatePresence mode="popLayout">
            {tickets.map((ticket, ticketIndex) => (
              <motion.div
                key={ticket.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{
                  delay: ticketIndex * 0.03,
                  duration: 0.3,
                  type: "spring",
                  stiffness: 300,
                  damping: 25,
                }}
                layout
                layoutId={ticket.id}
              >
                <TicketCard ticket={ticket} />
              </motion.div>
            ))}
          </AnimatePresence>

          {tickets.length === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex items-center justify-center text-gray-400 text-xs py-8"
            >
              {language["NO_TICKETS"]}
            </motion.div>
          )}
        </div>
      </div>
    </motion.div>
  );
}

export function WeekView({
  tickets,
  statuses,
  currentWeekStart,
  onTicketMove,
  darkMode,
  rangeStart,
  rangeEnd,
  useCustomRange,
}: WeekViewProps) {
  const getWeekDates = (startDate: Date) => {
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const weekDates = getWeekDates(currentWeekStart);

  const getTicketsForStatus = (statusId: string) => {
    return tickets.filter((ticket) => ticket.statusId === statusId);
  };

  const getRangeText = () => {
    if (useCustomRange && rangeStart && rangeEnd) {
      return `${rangeStart.toLocaleDateString("sq-AL", {
        day: "numeric",
        month: "long",
      })} - ${rangeEnd.toLocaleDateString("sq-AL", { day: "numeric", month: "long", year: "numeric" })}`;
    }

    const firstDay = weekDates[0];
    const lastDay = weekDates[6];
    return `${firstDay.toLocaleDateString("sq-AL", { day: "numeric", month: "long" })} - ${lastDay.toLocaleDateString(
      "sq-AL",
      { day: "numeric", month: "long", year: "numeric" }
    )}`;
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="flex-1 overflow-x-auto bg-gradient-to-br from-slate-50 via-white to-slate-50"
    >
      <div
        className="h-full"
        style={{ backgroundColor: darkMode ? "#555880" : "transparent", padding: 48, paddingTop: 20 }}
      >
        <div className="mb-4">
          <h2 className="text-gray-900 mb-1">{getRangeText()}</h2>
          <p className="text-sm text-gray-500">
            {tickets.length} {tickets.length === 1 ? "tiketë" : "tiketa"}
          </p>
        </div>

        <div className="flex gap-4" style={{ paddingBottom: 10 }}>
          {statuses.map((status, index) => {
            const statusTickets = getTicketsForStatus(status.id || "");

            return (
              <StatusColumn
                key={status.id}
                status={status}
                tickets={statusTickets}
                onTicketMove={onTicketMove}
                index={index}
              />
            );
          })}
        </div>
      </div>
    </motion.div>
  );
}
